import { useCallback, useRef, useState, type Ref } from "react";
import { useT } from "@/i18n";
import { wallMessages } from "@/i18n/messages/wall";
import { isTauri } from "@/lib/is-tauri";
import { useNotificationStore } from "@/stores/notification-store";
import { useProjectStore } from "@/stores/project-store";
import type { WallScope } from "@/stores/wall-scope-store";
import type { ContextMenuAnchor } from "@/components/ui/context-menu";
import { WallAddButton } from "./wall-add-menu";
import { WallNewPill } from "./wall-feed-states";
import { WallCloseButton, WallThemeButton } from "./wall-header-buttons";
import { WallNotifications } from "./wall-notifications";
import { WallScopeMenu } from "./wall-scope-menu";
import { WallSearchField } from "./wall-search-field";
import "./wall-surface.css";

interface WallHeaderProps {
  query: string;
  onQueryChange: (query: string) => void;
  searchRef?: Ref<HTMLInputElement>;
  scope: WallScope;
  onScopeChange: (scope: WallScope) => void;
  /** New work above the view (`useWallArrivals`): the pill under the field. */
  newCount: number;
  onShowNew: () => void;
  /** The wall's add menu shows; + opens it at the button. */
  addMenuOpen: boolean;
  onAddMenu: (anchor: ContextMenuAnchor) => void;
}

/**
 * The wall's sticky header (mockup `header()`, Part 09 decision 1): five
 * 40px pills in the wall chrome, centred: the scope, the search with its
 * notification dot, +, the theme and, on the desktop, × to close the
 * Workspace. No band behind them: the cards scroll under the pills, and
 * only the pills take the pointer.
 */
export function WallHeader({
  query,
  onQueryChange,
  searchRef,
  scope,
  onScopeChange,
  newCount,
  onShowNew,
  addMenuOpen,
  onAddMenu,
}: WallHeaderProps) {
  const t = useT(wallMessages);
  const fieldRef = useRef<HTMLDivElement>(null);
  const notificationsButtonRef = useRef<HTMLButtonElement>(null);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const closeNotifications = useCallback(() => setNotificationsOpen(false), []);
  const unread = useNotificationStore((state) => state.summary.count);
  const projectName = useProjectStore((state) => state.currentProject?.name);
  const placeholder =
    scope.kind === "archived"
      ? t("searchArchived")
      : scope.kind === "all"
        ? t("searchEverything")
        : projectName
          ? t("searchProject", { project: projectName })
          : t("searchThisProject");

  return (
    <div
      // Only the bar takes the pointer; cards stay clickable around it.
      className="pointer-events-none sticky top-0 z-20 px-appkit-md pb-10 pt-5"
      data-testid="wall-header"
    >
      <div
        className="pointer-events-auto mx-auto flex max-w-[760px] flex-wrap items-center justify-center gap-2"
        data-interactive="true"
        data-testid="wall-header-bar"
      >
        <WallScopeMenu scope={scope} onScopeChange={onScopeChange} />
        <div
          ref={fieldRef}
          className="relative flex min-w-0 max-w-[420px] flex-[1_1_220px]"
        >
          <WallSearchField
            ref={searchRef}
            query={query}
            onQueryChange={onQueryChange}
            placeholder={placeholder}
            unread={unread}
            notificationsOpen={notificationsOpen}
            onToggleNotifications={() => setNotificationsOpen((open) => !open)}
            notificationsButtonRef={notificationsButtonRef}
          />
          {query.trim() ? null : (
            <WallNewPill count={newCount} onShow={onShowNew} />
          )}
          {notificationsOpen ? (
            <WallNotifications
              query={query}
              anchorRef={fieldRef}
              returnFocusRef={notificationsButtonRef}
              onClose={closeNotifications}
            />
          ) : null}
        </div>
        <WallAddButton open={addMenuOpen} onOpen={onAddMenu} />
        <WallThemeButton />
        {isTauri() ? <WallCloseButton /> : null}
      </div>
    </div>
  );
}
